import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Flex,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Text,
    VStack,
    HStack,
    Badge,
    useBreakpointValue,
    ButtonGroup,
    Grid,
} from "@chakra-ui/react";
import {
    MdOutlineKeyboardArrowLeft,
    MdOutlineKeyboardArrowRight,
    MdMyLocation,
    MdOutlineLegendToggle,
} from "react-icons/md";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { HotspotData, HotspotFilterState } from "../types/hotspots";
import { getSeverityColor, formatDateTime } from "../utils/hotspots";
import { useHotspots } from "../hooks/useHotspots";
import { FiMinus, FiPlus } from "react-icons/fi";
import FilterHotspots from "../components/FilterHotspots";

const getBadgeColorScheme = (severity?: number): string => {
    switch (severity) {
        case 4:
            return "red";
        case 3:
            return "orange";
        case 2:
            return "yellow";
        case 1:
            return "green";
        default:
            return "gray";
    }
};

function getConfidenceLabel(confidence: string) {
    switch (confidence) {
        case "H":
            return "Tinggi";
        case "N":
            return "Sedang";
        case "L":
            return "Rendah";
        default:
            return "Tidak Diketahui";
    }
}

const MapHotspotsComponent = () => {
    const [selectedHotspot, setSelectedHotspot] = useState<HotspotData | null>(
        null
    );
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<maplibregl.Map | null>(null);
    const [pendingApply, setPendingApply] = useState(false);
    const [isSatellite, setIsSatellite] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isLegendOpen, setIsLegendOpen] = useState(false);

    const {
        hotspots,
        loading,
        error,
        filters,
        fetchHotspots,
        updateFilters, // Commented out - not used currently
        clearFilters: clearHotspotsFilters, // Commented out - not used currently
    } = useHotspots();

    const defaultSidebarState = useBreakpointValue({ base: false, md: false });

    useEffect(() => {
        setSidebarOpen(defaultSidebarState ?? false);
    }, [defaultSidebarState]);

    const handleFilterChange = useCallback(
        (newFilters: HotspotFilterState) => {
            updateFilters(newFilters);
        },
        [updateFilters]
    );

    const clearFilters = useCallback(() => {
        clearHotspotsFilters();
        setPendingApply(true);
    }, [clearHotspotsFilters]);

    const handleApplyFilters = useCallback(() => {
        setPendingApply(true);
    }, []);

    useEffect(() => {
        if (!mapContainerRef.current) return;

        // const mapInstance = new maplibregl.Map({
        //     container: mapContainerRef.current,
        //     style: "https://demotiles.maplibre.org/style.json",
        //     center: [104.211177, -3.141669],
        //     zoom: 8,
        //     attributionControl: false,
        // });
        const mapInstance = new maplibregl.Map({
            container: mapContainerRef.current,
            style: "https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK",
            center: [104.211177, -3.141669],
            zoom: 10,
            attributionControl: false,
        });

        mapInstance.on("load", async () => {
            mapInstance.addSource("satellite", {
                type: "raster",
                tiles: [
                    "https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=Q3fXi2sghTMg1wQSFgxK",
                ],
                tileSize: 256,
            });
            mapInstance.addLayer({
                id: "satellite-layer",
                type: "raster",
                source: "satellite",
                layout: { visibility: "visible" },
            });

            // Then add GeoJSON and hotspots layers (so they appear on top)
            await loadGeoJSON(mapInstance);
            await fetchHotspotsData(mapInstance);

            // Ensure GeoJSON and hotspots layers are above satellite by default
            if (mapInstance.getLayer("geojson-layer")) {
                mapInstance.moveLayer("geojson-layer");
            }
            if (mapInstance.getLayer("geojson-label")) {
                mapInstance.moveLayer("geojson-label");
            }
            if (mapInstance.getLayer("lkk-layer")) {
                mapInstance.moveLayer("lkk-layer");
            }
            if (mapInstance.getLayer("lkk-label")) {
                mapInstance.moveLayer("lkk-label");
            }
            if (mapInstance.getLayer("mrn-layer")) {
                mapInstance.moveLayer("mrn-layer");
            }
            if (mapInstance.getLayer("mrn-label")) {
                mapInstance.moveLayer("mrn-label");
            }
            if (mapInstance.getLayer("hotspots-marker")) {
                mapInstance.moveLayer("hotspots-marker");
            }
        });

        setMap(mapInstance);

        return () => {
            if (mapInstance) {
                mapInstance.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (!map) return;
        if (!pendingApply) return;

        fetchHotspotsData(map, filters)
            .catch(console.error)
            .finally(() => {
                setPendingApply(false);
            });
    }, [map, pendingApply, filters]);

    const loadGeoJSON = async (mapInstance: maplibregl.Map) => {
        try {
            // Load batas blok kebun GBS
            const response = await fetch("/data/batas_blok_kebun_gbs.geojson");
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const geojsonData = await response.json();

            if (!mapInstance.getSource("geojson-source")) {
                mapInstance.addSource("geojson-source", {
                    type: "geojson",
                    data: geojsonData,
                });
                mapInstance.addLayer({
                    id: "geojson-layer",
                    type: "fill",
                    source: "geojson-source",
                    paint: {
                        "fill-color": "#ffffff",
                        "fill-opacity": 0.3,
                        "fill-outline-color": "#000000",
                    },
                });
                mapInstance.addLayer({
                    id: "geojson-label",
                    type: "symbol",
                    source: "geojson-source",
                    layout: {
                        "text-field": ["get", "LandUse_26"],
                        "text-size": 14,
                        "text-anchor": "center",
                        "text-offset": [0, 0],
                    },
                    paint: {
                        "text-color": "#000000",
                        "text-halo-width": 2,
                    },
                });
            }

            // Load Blok Planted LKK
            const lkkResponse = await fetch("/data/Blok_Planted_LKK.geojson");
            if (!lkkResponse.ok) {
                throw new Error(`HTTP error! Status: ${lkkResponse.status}`);
            }

            const lkkData = await lkkResponse.json();

            // Set warna default untuk LKK blocks
            lkkData.features.forEach((feature: any) => {
                feature.properties.color = '#ffffff';
            });

            if (!mapInstance.getSource("lkk-source")) {
                mapInstance.addSource("lkk-source", {
                    type: "geojson",
                    data: lkkData
                });
                mapInstance.addLayer({
                    id: "lkk-layer",
                    type: "fill",
                    source: "lkk-source",
                    paint: {
                        "fill-color": "#ffffff",
                        "fill-opacity": 0.3,
                        "fill-outline-color": "#000000",
                    },
                });
                mapInstance.addLayer({
                    id: "lkk-label",
                    type: "symbol",
                    source: "lkk-source",
                    layout: {
                        "text-field": ["get", "blok_code"],
                        "text-size": 14,
                        "text-anchor": "center",
                        "text-offset": [0, 0],
                    },
                    paint: {
                        "text-color": "#000000",
                        "text-halo-width": 2,
                    },
                });
            } else {
                (mapInstance.getSource("lkk-source") as maplibregl.GeoJSONSource).setData(lkkData);
            }

            // Load Batas MRN
            const mrnResponse = await fetch("/data/mrn_map.geojson");
            if (mrnResponse.ok) {
                const mrnData = await mrnResponse.json();
                mrnData.features.forEach((feature: any) => {
                    feature.properties.color = '#ffffff';
                });

                if (!mapInstance.getSource("mrn-source")) {
                    mapInstance.addSource("mrn-source", {
                        type: "geojson",
                        data: mrnData
                    });
                    mapInstance.addLayer({
                        id: "mrn-layer",
                        type: "fill",
                        source: "mrn-source",
                        paint: {
                            "fill-color": "#ffffff",
                            "fill-opacity": 0.3,
                            "fill-outline-color": "#000000",
                        },
                    });
                    mapInstance.addLayer({
                        id: "mrn-label",
                        type: "symbol",
                        source: "mrn-source",
                        layout: {
                            "text-field": ["coalesce", ["get", "blok_code"], ["get", "LandUse_26"], ""],
                            "text-size": 14,
                            "text-anchor": "center",
                            "text-offset": [0, 0],
                        },
                        paint: {
                            "text-color": "#000000",
                            "text-halo-width": 2,
                        },
                    });
                } else {
                    (mapInstance.getSource("mrn-source") as maplibregl.GeoJSONSource).setData(mrnData);
                }
            }
        } catch (error) {
            console.error("Error loading GeoJSON file:", error);
        }
    };

    const fetchHotspotsData = async (
        mapInstance: maplibregl.Map,
        filterParams?: HotspotFilterState
    ) => {
        try {
            // Remove existing hotspot layers
            if (mapInstance.getLayer("hotspots-marker")) {
                mapInstance.removeLayer("hotspots-marker");
            }
            if (mapInstance.getSource("hotspots-source")) {
                mapInstance.removeSource("hotspots-source");
            }

            const filtersToUse = filterParams || filters;
            console.log("Fetching hotspots data with params:", filtersToUse);

            // Use the hook's fetchHotspots method
            const hotspots = await fetchHotspots(filtersToUse);

            const markerGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Point, any> = {
                type: "FeatureCollection",
                features: hotspots.map((hotspot) => {
                    const severityColor = getSeverityColor(hotspot.severity);

                    return {
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [hotspot.longitude, hotspot.latitude],
                        },
                        properties: {
                            ...hotspot,
                            severity_color: severityColor,
                        },
                    };
                }),
            };

            mapInstance.addSource("hotspots-source", {
                type: "geojson",
                data: markerGeoJSON,
            });

            mapInstance.addLayer({
                id: "hotspots-marker",
                type: "circle",
                source: "hotspots-source",
                paint: {
                    "circle-radius": 8,
                    "circle-color": ["get", "severity_color"],
                    "circle-opacity": 0.8,
                    "circle-stroke-width": 2,
                    "circle-stroke-color": "#000",
                },
            });

            // Add click event
            mapInstance.on("click", "hotspots-marker", (e) => {
                if (e.features && e.features.length > 0) {
                    const feature = e.features[0];
                    handleMarkerClick(feature.properties);
                }
            });

            // Change cursor on hover
            mapInstance.on("mouseenter", "hotspots-marker", () => {
                mapInstance.getCanvas().style.cursor = "pointer";
            });

            mapInstance.on("mouseleave", "hotspots-marker", () => {
                mapInstance.getCanvas().style.cursor = "";
            });
        } catch (error) {
            console.error("Error fetching hotspots data:", error);
        }
    };

    const handleMarkerClick = useCallback((properties: any) => {
        if (!properties) return;

        const hotspot: HotspotData = {
            id: Number(properties.id),
            latitude: Number(properties.latitude),
            longitude: Number(properties.longitude),
            acq_datetime_utc: properties.acq_datetime_utc,
            acq_datetime_wib: properties.acq_datetime_wib,
            satellite: properties.satellite,
            instrument: properties.instrument,
            confidence: properties.confidence,
            frp: Number(properties.frp),
            daynight: properties.daynight,
            distance_to_plantation_km: properties.distance_to_plantation_km !== undefined && properties.distance_to_plantation_km !== null
                ? Number(properties.distance_to_plantation_km)
                : null,
            nearest_plantation_id: properties.nearest_plantation_id !== undefined && properties.nearest_plantation_id !== null
                ? Number(properties.nearest_plantation_id)
                : null,
            nearest_plantation_name: properties.nearest_plantation_name || "-",
            severity: Number(properties.severity),
            severity_label: properties.severity_label,
            date_last_broadcast: properties.date_last_broadcast,
            is_inside_plantation: typeof properties.is_inside_plantation === "string"
                ? properties.is_inside_plantation === "true"
                : Boolean(properties.is_inside_plantation),
            processed_at: properties.processed_at,
            source_api: properties.source_api,
        };

        setSelectedHotspot(hotspot);
    }, []);

    const focusToArea = () => {
        if (map) {
            map.flyTo({
                center: [104.211177, -3.141669],
                zoom: 13,
                essential: true,
            });
        }
    };

    const zoomIn = () => {
        if (map) map.zoomIn();
    };

    const zoomOut = () => {
        if (map) map.zoomOut();
    };

    const toggleSatellite = () => {
        if (!map) return;
        const currentVisibility = map.getLayoutProperty(
            "satellite-layer",
            "visibility"
        );
        if (currentVisibility === "visible") {
            map.setLayoutProperty("satellite-layer", "visibility", "none");
            setIsSatellite(false);
        } else {
            map.setLayoutProperty("satellite-layer", "visibility", "visible");
            setIsSatellite(true);

            // Ensure GeoJSON and hotspots layers are above satellite
            if (map.getLayer("geojson-layer")) {
                map.moveLayer("geojson-layer");
            }
            if (map.getLayer("geojson-label")) {
                map.moveLayer("geojson-label");
            }
            if (map.getLayer("lkk-layer")) {
                map.moveLayer("lkk-layer");
            }
            if (map.getLayer("lkk-label")) {
                map.moveLayer("lkk-label");
            }
            if (map.getLayer("hotspots-marker")) {
                map.moveLayer("hotspots-marker");
            }
        }
    };


    return (
        <Box position="relative" h="100%" overflow="hidden">
            <Flex h="full">
                {/* Sidebar */}
                <Box
                    position={{ base: "fixed", md: "fixed" }}
                    top={{ base: "60px", md: "74px" }}
                    left={{ base: sidebarOpen ? 0 : "-100%", md: sidebarOpen ? 0 : -350 }}
                    width={{ base: "calc(100% - 64px)", md: "350px" }}
                    height={{ base: "calc(100dvh - 60px)", md: "calc(100dvh - 74px)" }}
                    bg="white"
                    boxShadow="lg"
                    zIndex={15}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    overflow="hidden"
                >
                    {sidebarOpen && (
                        <FilterHotspots
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={clearFilters}
                            onApplyFilters={handleApplyFilters}
                            hotspots={hotspots}
                            loading={loading}
                            error={error}
                        />
                    )}
                </Box>

                {/* Toggle Sidebar Button */}
                <Box
                    position="fixed"
                    top={{ base: "48px", md: "34px" }}
                    left={{
                        base: sidebarOpen ? "calc(100% - 56px)" : "10px",
                        md: sidebarOpen ? "360px" : "10px",
                    }}
                    zIndex={200}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    {sidebarOpen ? (
                        <IconButton
                            aria-label="Sembunyikan Sidebar"
                            icon={<MdOutlineKeyboardArrowLeft />}
                            size="lg"
                            top={{ base: 5, md: 10 }}
                            onClick={() => setSidebarOpen(false)}
                            borderRadius="md"
                            boxShadow="md"
                            // bg="teal.500"
                            color="white"
                            _hover={{ bg: "customGreen.600" }}
                        />
                    ) : (
                        <IconButton
                            aria-label="Tampilkan Sidebar"
                            icon={<MdOutlineKeyboardArrowRight />}
                            size="lg"
                            top={{ base: "20px", md: 10 }}
                            onClick={() => setSidebarOpen(true)}
                            borderRadius="md"
                            boxShadow="md"
                            bg="customGreen.500"
                            color="white"
                            _hover={{ bg: "customGreen.600" }}
                        />
                    )}
                </Box>

                {/* Map Container */}
                <Box
                    flex={1}
                    position="relative"
                    ml={{ base: 0, md: sidebarOpen ? "350px" : 0 }}
                >
                    <Box
                        ref={mapContainerRef}
                        id="map"
                        h="full"
                        w="full"
                        sx={{
                            ".maplibre-gl-canvas": {
                                transition: "filter 0.3s",
                            },
                            "&:hover .maplibre-gl-canvas": {
                                filter: "brightness(0.98)",
                            },
                        }}
                    />

                    {/* Stats moved to sidebar - keeping this commented for potential future use */}
                    {/* <Box
                        position="absolute"
                        bottom={{ base: "12px", md: "20px" }}
                        left={{ base: "30%", md: "20px" }}
                        transform={{ base: "translateX(-50%)", md: "none" }}
                        zIndex={20}
                        width={{ base: "90vw", sm: "340px", md: "300px" }}
                        minW={{ base: "100", sm: "260px", md: "260px" }}
                        maxW={{ base: "98vw", sm: "300px", md: "320px" }}
                        height={{ base: "auto", sm: "auto", md: "auto" }}
                        maxH={{ base: "220px", sm: "260px", md: "320px" }}
                        overflowY="auto"
                    >
                        <HotspotsStats isSatellite={isSatellite} />
                    </Box> */}

                    <Flex
                        position="absolute"
                        bottom={{ base: 4, sm: 6, md: 8 }}
                        right={{ base: 4, sm: 3, md: 8 }}
                        direction="column"
                        gap={2}
                        zIndex={10}
                        sx={{ bottom: { base: "max(16px, env(safe-area-inset-bottom, 16px))", sm: "24px", md: "32px" } }}
                    >
                        <IconButton
                            aria-label="Show Legend"
                            icon={<MdOutlineLegendToggle />}
                            onClick={() => setIsLegendOpen(true)}
                            variant="solid"
                            colorScheme="green"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"
                            h="40px"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            mb={2}
                        />
                        <IconButton
                            aria-label="Toggle Satelit"
                            icon={<Text fontSize="x-small">{isSatellite ? "Satelit" : "Peta"}</Text>}
                            onClick={toggleSatellite}
                            variant="solid"
                            colorScheme="blue"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"  // Lebar tetap
                            h="40px"  // Tinggi tetap
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                        />
                        <IconButton
                            aria-label="Focus area"
                            icon={<MdMyLocation size={20} />}
                            onClick={focusToArea}
                            variant="solid"
                            colorScheme="blue"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"
                            h="40px"
                        />
                        <ButtonGroup
                            variant="solid"
                            colorScheme="blue"
                            isAttached
                            borderRadius="full"
                            boxShadow="lg"
                            orientation="vertical"
                        >
                            <IconButton
                                aria-label="Zoom in"
                                icon={<FiPlus size={20} />}
                                onClick={zoomIn}
                                borderBottomRadius="0"
                                w="40px"
                                h="40px"
                            />
                            <IconButton
                                aria-label="Zoom out"
                                icon={<FiMinus size={20} />}
                                onClick={zoomOut}
                                borderTopRadius="0"
                                w="40px"
                                h="40px"
                            />
                        </ButtonGroup>
                    </Flex>
                </Box>
            </Flex>

            {/* Detail Modal */}
            <Modal
                isOpen={!!selectedHotspot}
                onClose={() => setSelectedHotspot(null)}
                size={{ base: "lg", md: "lg" }}
                isCentered
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent borderRadius="lg" overflow="hidden">
                    <ModalHeader bg="customGold.500" borderBottomWidth="1px" borderColor="gray.100">
                        <HStack justify="space-between">
                            <Text color={"white"} >Detail Hotspot</Text>
                            {/* <Text>Detail Hotspot #{selectedHotspot?.id}</Text> */}
                            <Badge
                                colorScheme={getBadgeColorScheme(selectedHotspot?.severity)}
                                variant="solid"
                                mr={4}
                            >
                                {selectedHotspot?.severity_label}
                            </Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody p={6}>
                        {selectedHotspot && (
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <DetailItem
                                    label="Satelit"
                                    value={`${selectedHotspot.satellite} (${selectedHotspot.instrument})`}
                                />
                                <DetailItem
                                    label="Koordinat"
                                    value={`${selectedHotspot.latitude.toFixed(
                                        6
                                    )}, ${selectedHotspot.longitude.toFixed(6)}`}
                                />
                                <DetailItem
                                    label="Waktu Deteksi (UTC)"
                                    value={(() => {
                                        const { date, time } = formatDateTime(
                                            selectedHotspot.acq_datetime_utc
                                        );
                                        return `${date} ${time}`;
                                    })()}
                                />
                                <DetailItem
                                    label="Waktu Deteksi (WIB)"
                                    value={(() => {
                                        const { date, time } = formatDateTime(
                                            selectedHotspot.acq_datetime_wib
                                        );
                                        return `${date} ${time}`;
                                    })()}
                                />
                                <DetailItem
                                    label="Fire Radiative Power (FRP)"
                                    value={`${selectedHotspot.frp} MW`}
                                />
                                <DetailItem
                                    label="Confidence"
                                    value={getConfidenceLabel(selectedHotspot.confidence)}
                                />
                                <DetailItem
                                    label="Day/Night"
                                    value={selectedHotspot.daynight === "D" ? "Day" : "Night"}
                                />
                                <DetailItem
                                    label="Jarak ke Estate Terdekat"
                                    value={
                                        selectedHotspot.distance_to_plantation_km !== null &&
                                        selectedHotspot.distance_to_plantation_km !== undefined
                                            ? `${selectedHotspot.distance_to_plantation_km.toFixed(2)} km`
                                            : "-"
                                    }
                                />
                                <DetailItem
                                    label="Estate Terdekat"
                                    value={selectedHotspot.nearest_plantation_name}
                                />
                                <DetailItem
                                    label="Dalam Area Perkebunan"
                                    value={selectedHotspot.is_inside_plantation ? "Ya" : "Tidak"}
                                />
                                <DetailItem
                                    label="Sumber"
                                    value={selectedHotspot.source_api}
                                />
                            </Grid>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Legend Modal */}
            <Modal
                isOpen={isLegendOpen}
                onClose={() => setIsLegendOpen(false)}
                size="md"
                isCentered
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent borderRadius="2xl" overflow="hidden">
                    <ModalHeader bg="customGold.500" color="white">
                        Legend Titik Panas
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody p={6}>
                        <VStack spacing={3} align="stretch">
                            <Text fontWeight="bold" mb={2}>
                                Tingkat Keparahan:
                            </Text>
                            <LegendItem
                                color="#FF0000"
                                description="Bahaya - Di dalam area"
                            />
                            <LegendItem
                                color="#FF8C00"
                                description="Waspada - 0-1 km"
                            />
                            <LegendItem
                                color="#FFD700"
                                description="Perhatian - 1-5 km"
                            />
                            <LegendItem color="#00FF00" description="Aman > 5 km" />
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

const DetailItem = ({
    label,
    value,
}: {
    label: string;
    value?: string | React.ReactNode;
}) => (
    <Box>
        <Text fontSize="sm" color="gray.500" mb={1}>
            {label}
        </Text>
        {typeof value === "string" ? (
            <Text fontWeight="500">{value || "-"}</Text>
        ) : (
            value || <Text fontWeight="500">-</Text>
        )}
    </Box>
);

const LegendItem = ({
    color,
    description,
}: {
    color: string;
    description: string;
}) => (
    <HStack>
        <Box
            w="20px"
            h="20px"
            bg={color}
            borderRadius="full"
            border="1px solid gray"
        />
        <Text fontSize="sm" color="gray.700">
            {description}
        </Text>
    </HStack>
);

export default MapHotspotsComponent;
